// src/pages/finance-portal/stripe-issuing/StripeIssuing.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, CircularProgress, Skeleton, Tooltip, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val, currency = 'usd') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format((val || 0) / 100);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const CARD_STATUS_COLORS = {
    active: { bg: '#dcfce7', color: '#166534' },
    inactive: { bg: '#fef3c7', color: '#92400e' },
    canceled: { bg: '#fecaca', color: '#991b1b' },
};

export default function StripeIssuing() {
    const [cards, setCards] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [txLoading, setTxLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Create card dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        currency: 'usd',
        spending_limit_amount: '',
        spending_limit_interval: 'monthly',
    });
    const [creating, setCreating] = useState(false);

    const fetchCards = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/issuing/cards');
            if (res.data.success) {
                setCards(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load cards');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchTransactions = useCallback(async () => {
        setTxLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/issuing/transactions');
            if (res.data.success) {
                setTransactions(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load transactions');
        } finally {
            setTxLoading(false);
        }
    }, []);

    useEffect(() => { fetchCards(); fetchTransactions(); }, [fetchCards, fetchTransactions]);

    const handleCreateCard = async () => {
        setCreating(true);
        try {
            const payload = {
                type: 'virtual',
                currency: createForm.currency,
            };
            if (createForm.spending_limit_amount) {
                payload.spending_controls = {
                    spending_limits: [{
                        amount: Math.round(parseFloat(createForm.spending_limit_amount) * 100),
                        interval: createForm.spending_limit_interval,
                    }],
                };
            }
            await axiosInstance.post('/api/finance/stripe/issuing/cards', payload);
            setSuccess('Virtual card created');
            setCreateOpen(false);
            setCreateForm({ currency: 'usd', spending_limit_amount: '', spending_limit_interval: 'monthly' });
            fetchCards();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create card');
        } finally {
            setCreating(false);
        }
    };

    const getSpendingLimits = (card) => {
        const limits = card.spending_controls?.spending_limits;
        if (!limits || limits.length === 0) return null;
        return limits.map(l => `${formatCurrency(l.amount, card.currency || 'usd')} / ${l.interval}`).join(', ');
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CreditCardIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Stripe Issuing</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Virtual cards & transactions</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => { fetchCards(); fetchTransactions(); }}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Refresh
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Create Card
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="Cards" />
                    <Tab label="Transactions" />
                </Tabs>

                {/* Cards Tab */}
                {tabValue === 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Card ID', 'Last 4', 'Type', 'Spending Limits', 'Status', 'Created'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={6}><Skeleton height={36} /></TableCell></TableRow>)
                                    ) : cards.length === 0 ? (
                                        <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <CreditCardIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No cards found</Typography>
                                        </TableCell></TableRow>
                                    ) : cards.map(card => {
                                        const statusStyle = CARD_STATUS_COLORS[card.status] || { bg: '#f1f5f9', color: '#64748b' };
                                        return (
                                            <TableRow key={card.id} hover>
                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem', fontFamily: 'monospace' }}>{card.id}</TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                                                    **** {card.last4}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textTransform: 'capitalize' }}>
                                                    {card.type}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                    {getSpendingLimits(card) || <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>None</Typography>}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={card.status} size="small"
                                                        sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                    {formatDate(card.created)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}

                {/* Transactions Tab */}
                {tabValue === 1 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Transaction ID', 'Card', 'Merchant', 'Amount', 'Type', 'Created'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {txLoading ? (
                                        [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={6}><Skeleton height={36} /></TableCell></TableRow>)
                                    ) : transactions.length === 0 ? (
                                        <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No transactions found</Typography>
                                        </TableCell></TableRow>
                                    ) : transactions.map(tx => (
                                        <TableRow key={tx.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', fontFamily: 'monospace' }}>{tx.id}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                **** {tx.card?.last4 || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>
                                                {tx.merchant_data?.name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                                                {formatCurrency(Math.abs(tx.amount), tx.currency || 'usd')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textTransform: 'capitalize' }}>
                                                {tx.type?.replace(/_/g, ' ') || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(tx.created)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}
            </Box>

            {/* Create Virtual Card Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Create Virtual Card</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontFamily }}>Currency</InputLabel>
                        <Select value={createForm.currency} onChange={e => setCreateForm(prev => ({ ...prev, currency: e.target.value }))}
                            label="Currency" sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {['usd', 'eur', 'gbp'].map(c => <MenuItem key={c} value={c} sx={{ fontFamily }}>{c.toUpperCase()}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Spending Limit (optional)</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Amount" value={createForm.spending_limit_amount}
                            onChange={e => setCreateForm(prev => ({ ...prev, spending_limit_amount: e.target.value }))}
                            size="small" type="number" sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                sx: { fontFamily, borderRadius: '10px' },
                                inputProps: { min: 0, step: '0.01' },
                            }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel sx={{ fontFamily }}>Interval</InputLabel>
                            <Select value={createForm.spending_limit_interval}
                                onChange={e => setCreateForm(prev => ({ ...prev, spending_limit_interval: e.target.value }))}
                                label="Interval" sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {['per_authorization', 'daily', 'weekly', 'monthly', 'yearly', 'all_time'].map(i => (
                                    <MenuItem key={i} value={i} sx={{ fontFamily }}>{i.replace(/_/g, ' ')}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateCard} disabled={creating}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Card'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
